import prepareTranslate from '@/helpers/dictionary';
import { ROLE_LIST, USER_STATUS } from '@/helpers/enums';
import { isEmail, isPassword, isPhone } from '@/helpers/is';
import {
  Button,
  Grid,
  Group,
  PasswordInput,
  Progress,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import userMutations from '@/api/mutitations/user';
import { getStrength } from '@/pages/ResetPassword/funcs';
import DetailsModal from '../DetailsModal';
import Modal from '../Modal/Index';

const UserCreate = (props) => {
  const {
    opened,
    close: modalClose,
    setKey,
  } = props;

  const t = prepareTranslate();
  const [loading, { open, close }] = useDisclosure(false);
  const { useCreateUser } = userMutations;
  const { mutateAsync } = useCreateUser();

  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const resultActions = [
    <Button key='closeButton' w={'7rem'} onClick={() => actionResultClose()}>{t('global.close')}</Button>,
  ];

  const [resultLabel, setResultLabel] = useState('');

  // eslint-disable-next-line consistent-return
  const handleUserCreateClick = async (values) => {
    try {
      open();
      // eslint-disable-next-line no-unused-vars
      const { confirmPassword, isArchived, ...userValues } = values;
      const newUser = await mutateAsync(userValues);

      if (!newUser) {
        setResultLabel(t('pages.usercreate.notification.errortitle'));
        actionResultOpen();
        close();
        return null;
      }
      setResultLabel(t('pages.usercreate.notification.title'));
      close();
      modalClose();
      actionResultOpen();
      setKey((prev) => prev + 1);
      form.reset();
      close();
    } catch (error) {
      setResultLabel(t('pages.usercreate.notification.errortitle'));
      actionResultOpen();
      close();
    }
  };

  const handleUserCancelClick = () => {
    form.reset();
    modalClose();
  };

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      userName: '',
      password: '',
      confirmPassword: '',
      role: 'administrator',
      isArchived: 'active',
      email: '',
      phone: '',
    },
    validate: {
      firstName: (value) => (value ? null : t('pages.usercreate.validations.firstname')),
      lastName: (value) => (value ? null : t('pages.usercreate.validations.lastname')),
      userName: (value) => (value ? null : t('pages.usercreate.validations.username')),
      password: (value) => (isPassword(value) ? null : t('pages.usercreate.validations.password.required')),
      confirmPassword: (value, values) => (value !== values.password ? t('pages.usercreate.validations.password.confirm') : null),
      role: (value) => (value ? null : t('pages.usercreate.validations.role')),
      isArchived: (value) => (value ? null : t('pages.usercreate.validations.status')),
      email: (value) => (isEmail(value) ? null : t('pages.usercreate.validations.email')),
      phone: (value) => (isPhone(value) ? null : t('pages.usercreate.validations.phone')),
    },
  });

  const strength = getStrength(form.values.password);

  const bars = Array(4)
    .fill(0)
    .map((_, index) => (
      <Progress
        styles={{ section: { transitionDuration: '0ms' } }}
        // eslint-disable-next-line max-len, no-nested-ternary
        value={form.values.password > 0 && index === 0 ? 100 : strength >= ((index + 1) / 4) * 100 ? 100 : 0}
        // eslint-disable-next-line no-nested-ternary
        color={strength > 80 ? 'teal' : strength > 50 ? 'yellow' : 'red'}
        key={index}
        size="4"
      />
    ));

  const actions = [
    <Button key="primary" w={'7rem'} loading={loading} onClick={form.onSubmit(handleUserCreateClick)}>{t('global.update')}</Button>,
    <Button key="cancel" w={'7rem'} variant="outline" onClick={handleUserCancelClick}>{t('global.cancel')}</Button>,
  ];

  return (
<>
          <DetailsModal
              opened={opened}
              close={handleUserCancelClick}
              actions={actions}
              label={t('pages.usercreate.title')}
              >
          <form>
              <Grid gutter="lg">
                <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.firstname')}
                    required
                    {...form.getInputProps('firstName')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.lastname')}
                    required
                    {...form.getInputProps('lastName')}
                  />
                      </Grid.Col>
                      <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.phone')}
                    required
                    {...form.getInputProps('phone')}
                  />
                </Grid.Col>
                      <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.email')}
                    required
                    {...form.getInputProps('email')}
                  />
                      </Grid.Col>
                      <Grid.Col span={{ base: 3 }}>
                  <Select
                    label={t('pages.useredit.form.role')}
                    data={ROLE_LIST}
                    required
                    {...form.getInputProps('role')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 3 }}>
                  <Select
                    label={t('pages.useredit.form.status')}
                    data={USER_STATUS}
                    required
                    {...form.getInputProps('isArchived')}
                  />
                      </Grid.Col>

              </Grid>
                  <Text
                      fw={'bold'}
                      c={'var(--mantine-color-brand-10)'}
                      fs={'large'}
                      m={'var(--mantine-spacing-xl) 0 var(--mantine-spacing-md)'}>
                      {t('pages.useredit.form.chooseCredentials')}
                </Text>

                  <Grid gutter="lg">

                <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.username')}
                    required
                    {...form.getInputProps('userName')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 3 }}>
                  <PasswordInput
                    autoComplete="off"
                    label={t('pages.useredit.form.password')}
                    required={false}
                    {...form.getInputProps('password')}
                  />
                  <Group gap={5} grow mt="xs" mb="md">
                    {bars}
                  </Group>
                </Grid.Col>
                <Grid.Col span={{ base: 3 }}>
                  <PasswordInput
                    autoComplete="off"
                    label={t('pages.useredit.form.confirmpassword')}
                    required={false}
                    {...form.getInputProps('confirmPassword')}
                  />
                </Grid.Col>
              </Grid>

              </form>
          </DetailsModal>
          <Modal
        opened={actionResultOpended}
        close={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
      </>
  );
};

UserCreate.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
};

export default UserCreate;
